// Write half of the Cloudflare version split; `constants/qsetVersion.ts` is the
// read half. Gradual deployments route every request independently by
// percentage, so without a per-device key a browser would get v3 on one visit
// and v4 on the next. A Transform Rule on the zone copies this cookie into the
// `Cloudflare-Workers-Version-Key` header, which is what affinity keys on.
// Rejected alternatives and the dashboard setup: `docs/cloudflare-deploy.md`.
// What the pin does and does not guarantee: `docs/version-affinity.md`.

export const VERSION_KEY_COOKIE = "version-key";

/** One year, matching the cookie's stated lifetime in the consent dialog. */
export const VERSION_KEY_MAX_AGE = 31_536_000;

const hasVersionKey = (): boolean =>
  document.cookie
    .split("; ")
    .some((entry) => entry.startsWith(`${VERSION_KEY_COOKIE}=`));

/**
 * Writes the affinity cookie once, on first visit.
 *
 * Idempotent by design: overwriting the key would re-roll which arm the device
 * lands on, which is the one thing affinity exists to prevent. Prefix matching
 * is done on whole cookie entries so a `not-version-key` cookie cannot be
 * mistaken for this one.
 *
 * Strictly necessary, so it is not gated on cookie consent: gating it would
 * hand anyone refusing consent the inconsistent-version failures this prevents.
 *
 * Both guards degrade to no affinity rather than throwing on the app-start
 * path. `crypto.randomUUID` is absent in insecure contexts, and `Secure` means
 * plain-http dev hosts (`PORTLESS=0`) drop the cookie in Safari; neither
 * matters, because affinity only has to work on the two https custom domains.
 */
export const ensureVersionKeyCookie = (): void => {
  if (typeof document === "undefined") return;
  if (typeof crypto === "undefined") return;
  if (typeof crypto.randomUUID !== "function") return;
  if (hasVersionKey()) return;

  document.cookie =
    `${VERSION_KEY_COOKIE}=${crypto.randomUUID()}; ` +
    `Path=/; Max-Age=${VERSION_KEY_MAX_AGE}; Secure; SameSite=Lax`;
};
